import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, RefreshCw, RefreshCcw } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  PageHeader,
  StatusBadge,
  Switch,
  textColumn,
  dateColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'

interface QualityIssue {
  code: string
  message: string
  severity: 'error' | 'warning'
}

interface GpRow {
  id: string
  name: string
  bpType: 'customer' | 'supplier' | 'prospect'
  isActive: boolean
  sapCardCode: string | null
  sapCardType: string | null
  tanssId: string | null
  city: string | null
  country: string | null
  tags: unknown
  tenantCode: string | null
  syncedAt: string | null
  createdAt: string
  qualityScore: number
  qualityIssues: QualityIssue[]
}

interface GpListResponse {
  data: GpRow[]
  pagination: { page: number; limit: number; total: number; pages: number }
}

const TYPE_LABEL: Record<string, string> = {
  customer: 'Kunde',
  supplier: 'Lieferant',
  prospect: 'Prospect',
}

const TYPE_STATUS: Record<string, string> = {
  customer: 'active',
  supplier: 'info',
  prospect: 'pending',
}

function qualityVariant(score: number): string {
  if (score >= 75) return 'success'
  if (score >= 50) return 'warning'
  return 'danger'
}

const columns: ColumnDef<GpRow, unknown>[] = [
  {
    id: 'bpType',
    header: 'Typ',
    accessorKey: 'bpType',
    size: 110,
    cell: ({ getValue }) => {
      const v = getValue() as string
      return <StatusBadge status={TYPE_STATUS[v] ?? 'default'} label={TYPE_LABEL[v] ?? v} />
    },
  },
  textColumn<GpRow>('sapCardCode', 'SAP-Code', { size: 110 }),
  {
    id: 'tenantCode',
    header: () => <span className="block text-center">Mandant</span>,
    accessorKey: 'tenantCode',
    size: 90,
    cell: ({ getValue }) => {
      const code = getValue() as string | null
      if (!code) return <span className="block text-center">—</span>
      return <div className="flex justify-center"><StatusBadge status="neutral" label={code} /></div>
    },
  },
  textColumn<GpRow>('name', 'Name', { size: 260 }),
  textColumn<GpRow>('city', 'Stadt', { size: 130 }),
  {
    id: 'tags',
    header: 'Tags',
    accessorKey: 'tags',
    size: 160,
    enableSorting: false,
    cell: ({ getValue }) => {
      const raw = getValue()
      let tags: string[] = []
      if (Array.isArray(raw)) tags = raw as string[]
      else if (typeof raw === 'string' && raw.startsWith('[')) {
        try { tags = JSON.parse(raw) } catch { tags = [] }
      }
      if (!tags.length) return '—'
      return (
        <span className="flex flex-wrap gap-1">
          {tags.slice(0, 3).map((t) => (
            <span key={t} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
              {t}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="text-xs text-gray-400">+{tags.length - 3}</span>
          )}
        </span>
      )
    },
  },
  {
    id: 'qualityScore',
    header: 'Qualität',
    accessorKey: 'qualityScore',
    size: 90,
    cell: ({ getValue }) => {
      const score = (getValue() as number) ?? 0
      return (
        <StatusBadge
          status={qualityVariant(score)}
          label={`${score} %`}
        />
      )
    },
  },
  dateColumn<GpRow>('syncedAt', 'Letzter Sync', { size: 130, withTime: false }),
]

type TypeFilter = '' | 'customer' | 'supplier' | 'prospect'

interface SyncAllResult {
  ok: boolean
  totals: {
    fetchedFromSap: number
    inserted: number
    updated: number
    unchanged: number
    errors: string[]
  }
}

export default function GpListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('')
  const [showInactive, setShowInactive] = useState(false)
  const { toast } = useToast()

  // Bei aktiver Suche immer inaktive mitladen (werden ausgegraut dargestellt)
  const includeInactive = showInactive || search.trim().length > 0

  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (typeFilter) params.set('type', typeFilter)
  if (includeInactive) params.set('includeInactive', 'true')
  params.set('limit', '200')

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<GpListResponse>({
    queryKey: ['business-partners', search, typeFilter, includeInactive, activeTenantIds],
    queryFn: () => apiClient.get(`/business-partners?${params.toString()}`).then((r) => r.data),
  })

  const syncAllMutation = useMutation<SyncAllResult>({
    mutationFn: () => apiClient.post('/business-partners/sync-all', {}).then((r) => r.data as SyncAllResult),
    onSuccess: (result) => {
      const { inserted, updated, errors } = result.totals
      void queryClient.invalidateQueries({ queryKey: ['business-partners'] })
      if (errors.length > 0) {
        toast({
          tone: 'warning',
          title: `Sync abgeschlossen mit ${errors.length} Fehler${errors.length !== 1 ? 'n' : ''}`,
          description: `${inserted} neu, ${updated} aktualisiert — ${errors.slice(0, 2).join('; ')}`,
        })
      } else {
        toast({
          tone: 'success',
          title: 'SAP-Sync abgeschlossen',
          description: `${inserted} neu, ${updated} aktualisiert, ${result.totals.unchanged} unverändert`,
        })
      }
    },
    onError: (err) => {
      toast({ tone: 'danger', title: 'SAP-Sync fehlgeschlagen', description: (err as Error)?.message })
    },
  })

  function handleTypeToggle(t: TypeFilter) {
    setTypeFilter((prev) => (prev === t ? '' : t))
  }

  async function handleRefresh() {
    const result = await refetch()
    if (result.isError) {
      toast({ tone: 'danger', title: 'Fehler beim Aktualisieren', description: (result.error as Error)?.message })
    } else {
      toast({ tone: 'success', title: 'Liste aktualisiert' })
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Geschäftspartner"
        description="Alle Kunden, Lieferanten und Prospects"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { void handleRefresh() }}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => syncAllMutation.mutate()}
              disabled={syncAllMutation.isPending}
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${syncAllMutation.isPending ? 'animate-spin' : ''}`} />
              {syncAllMutation.isPending ? 'Synchronisiere …' : 'Alle synchronisieren'}
            </Button>
          </div>
        }
      />

      <FilterBar>
        <Input
          placeholder="Name oder SAP-Code suchen …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72"
        />
        <FilterChip
          active={typeFilter === 'customer'}
          onRemove={typeFilter === 'customer' ? () => handleTypeToggle('customer') : undefined}
          onClick={() => handleTypeToggle('customer')}
        >
          Kunden
        </FilterChip>
        <FilterChip
          active={typeFilter === 'supplier'}
          onRemove={typeFilter === 'supplier' ? () => handleTypeToggle('supplier') : undefined}
          onClick={() => handleTypeToggle('supplier')}
        >
          Lieferanten
        </FilterChip>
        <FilterChip
          active={typeFilter === 'prospect'}
          onRemove={typeFilter === 'prospect' ? () => handleTypeToggle('prospect') : undefined}
          onClick={() => handleTypeToggle('prospect')}
        >
          Prospects
        </FilterChip>
        <Switch
          label="Inaktive anzeigen"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
      </FilterBar>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState
          title="Fehler beim Laden"
          description={(error as Error)?.message ?? 'Unbekannter Fehler'}
        />
      ) : !data?.data.length ? (
        <EmptyState
          icon={<Building2 className="w-8 h-8" />}
          title="Keine Geschäftspartner gefunden"
          description={search ? 'Keine Treffer für die Suche.' : 'Die Datenbank enthält noch keine Einträge.'}
        />
      ) : (
        <DataTable
          data={data.data}
          columns={columns}
          onRowClick={(row) => navigate(`/gp/${row.id}`)}
          pagination={{ pageSize: 50 }}
          searchable={false}
          rowClassName={(row) => row.isActive ? undefined : 'opacity-40'}
        />
      )}
    </div>
  )
}
